// build: style — the lens rack. One card per style lens (STYLE_LENSES,
// optionally limited by config.lenses); ▶ on a card auditions the learner's
// OWN track under that lens via playTrackMix — same notes, different world,
// which is the whole point of the moment. Tap a card to pick it; saving calls
// onKeeper(keeperId, keeperName, { lensId }). No checks here — taste is the
// only judge — so "save keeper" lights as soon as a lens is picked.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { resumeAudio } from "../../../lib/homeKeyAudio";
import { STYLE_LENSES, playTrackMix } from "../../../lib/homeKeyGroove";

export default function StylePick({ moment, onReadyChange, onKeeper, keepers }) {
  const config = moment.config || {};
  const lenses = useMemo(() => {
    const ids = config.lenses || Object.keys(STYLE_LENSES);
    return ids.map((id) => STYLE_LENSES[id]).filter(Boolean);
  }, [config.lenses]);

  const [picked, setPicked] = useState(null);
  const [auditioning, setAuditioning] = useState(null); // lens id or null
  const [saved, setSaved] = useState(false);

  const playRef = useRef(null);
  const timerRef = useRef(null);

  const stopAudition = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setAuditioning(null);
  }, []);
  useEffect(() => () => stopAudition(), [stopAudition]);

  const ready = saved;
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  const audition = (lensId) => {
    if (auditioning === lensId) {
      stopAudition();
      return;
    }
    resumeAudio();
    stopAudition();
    // Every layer on — playTrackMix skips whatever the learner hasn't made
    // yet, and the lens supplies its own drums when there's no beat keeper.
    const handle = playTrackMix({ keepers: keepers || {}, lensId, layers: {} });
    playRef.current = handle;
    setAuditioning(lensId);
    // Two passes of the cycle is a fair audition — then let it breathe.
    const cycleMs = (handle && handle.cycleMs) || 4000;
    timerRef.current = setTimeout(stopAudition, cycleMs * 2 + 300);
  };

  const save = () => {
    if (!picked) return;
    stopAudition();
    onKeeper(moment.keeperId, moment.keeperName, { lensId: picked });
    setSaved(true);
  };

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "Same notes, three worlds. Audition your track through each lens, then pick the one that sounds like you."}
      </p>

      <div className="hk-choice-row" role="group" aria-label="style lenses">
        {lenses.map((lens) => {
          const isPicked = picked === lens.id;
          const isPlaying = auditioning === lens.id;
          return (
            <div
              key={lens.id}
              className={`hk-lens-card${isPicked ? " hk-lens-card--picked" : ""}`}
            >
              <button
                type="button"
                className="hk-lens-pick"
                aria-pressed={isPicked}
                onClick={() => setPicked(lens.id)}
                disabled={saved}
              >
                <span className="hk-lens-name">{lens.label || lens.id}</span>
                {lens.blurb && <span className="hk-lens-blurb">{lens.blurb}</span>}
                <span className="hk-lens-meta">{lens.bpm} bpm</span>
              </button>
              <button
                type="button"
                className={`hk-demo-btn${isPlaying ? " hk-demo-btn--on" : ""}`}
                onClick={() => audition(lens.id)}
                aria-label={`${isPlaying ? "stop" : "play"} my track as ${
                  lens.label || lens.id
                }`}
              >
                {isPlaying ? "■ stop" : "▶ my track, this way"}
              </button>
            </div>
          );
        })}
      </div>

      {!saved && (
        <div className="hk-choice-row">
          <button
            type="button"
            className="hk-btn hk-btn--primary"
            disabled={!picked}
            onClick={save}
          >
            save keeper
          </button>
        </div>
      )}

      {saved && (
        <p className="hk-echo-status">
          saved to your track: <strong>{moment.keeperName}</strong>. It's part of
          the music now.
        </p>
      )}
    </div>
  );
}
